import React from 'react';
import { motion } from 'framer-motion';
import { CheckCircleIcon, AlertCircleIcon, XCircleIcon, LinkIcon, ClockIcon, CheckIcon } from 'lucide-react';
interface ProductCardProps {
  product: Product;
  matchCategory: string;
  onSkip?: (id: string) => void;
  isSelectable?: boolean;
  isSelected?: boolean;
  onSelect?: (id: string) => void;
}
const getMatchIcon = matchCategory => {
  switch (matchCategory) {
    case 'perfect-match':
      return <CheckCircleIcon className="h-5 w-5 text-green-500" data-id="element-156" />;
    case 'potential-match':
      return <AlertCircleIcon className="h-5 w-5 text-yellow-500" data-id="element-157" />;
    case 'no-match':
      return <XCircleIcon className="h-5 w-5 text-red-500" data-id="element-158" />;
    default:
      return null;
  }
};
const getMatchText = matchCategory => {
  switch (matchCategory) {
    case 'perfect-match':
      return 'Perfect Match';
    case 'potential-match':
      return 'Potential Match';
    case 'no-match':
      return 'No Match';
    default:
      return '';
  }
};
const ProductCard = ({
  product,
  matchCategory,
  onSkip,
  isSelectable,
  isSelected,
  onSelect
}: ProductCardProps) => {
  const handleSkip = () => {
    onSkip?.(product.id);
  };
  const getActionButtons = () => {
    if (product.skipped) {
      return <>
          <motion.button className="px-4 py-2 bg-purple-600 text-white rounded-md flex items-center hover:bg-purple-700 transition-colors" whileHover={{
          scale: 1.05
        }} whileTap={{
          scale: 0.95
        }} data-id="element-159">
            <LinkIcon className="h-4 w-4 mr-1" data-id="element-160" />
            Review Now
          </motion.button>
          <div className="mt-2 text-xs text-gray-500 flex items-center justify-center" data-id="element-161">
            <ClockIcon className="h-3 w-3 mr-1" data-id="element-162" />
            Skipped on {new Date(product.skippedAt).toLocaleDateString()}
          </div>
        </>;
    }
    return <>
        <motion.button className="px-4 py-2 bg-blue-600 text-white rounded-md flex items-center hover:bg-blue-700 transition-colors" whileHover={{
        scale: 1.05
      }} whileTap={{
        scale: 0.95
      }} data-id="element-163">
          <LinkIcon className="h-4 w-4 mr-1" data-id="element-164" />
          Link Product
        </motion.button>
        {matchCategory === 'potential-match' && <motion.button className="mt-2 px-4 py-1 text-sm text-blue-600 hover:underline" whileHover={{
        scale: 1.05
      }} whileTap={{
        scale: 0.95
      }} data-id="element-165">
            View Suggestions
          </motion.button>}
        {matchCategory === 'no-match' && <div className="flex flex-col items-center" data-id="element-166">
            <motion.button className="mt-2 px-4 py-1 text-sm text-blue-600 hover:underline" whileHover={{
          scale: 1.05
        }} whileTap={{
          scale: 0.95
        }} data-id="element-167">
              Create Listing
            </motion.button>
            <motion.button onClick={handleSkip} className="mt-2 px-4 py-1 text-sm text-gray-500 hover:text-purple-600 hover:underline flex items-center" whileHover={{
          scale: 1.05
        }} whileTap={{
          scale: 0.95
        }} data-id="element-168">
              <ClockIcon className="h-3 w-3 mr-1" data-id="element-169" />
              Skip for Now
            </motion.button>
          </div>}
      </>;
  };
  return <motion.div className={`bg-white rounded-lg shadow p-4 flex flex-col md:flex-row ${product.skipped ? 'border border-purple-200' : ''} ${isSelected ? 'ring-2 ring-purple-500' : ''}`} initial={{
    opacity: 0,
    y: 20
  }} animate={{
    opacity: 1,
    y: 0
  }} whileHover={{
    y: -2,
    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)'
  }} transition={{
    duration: 0.2
  }} data-id="element-170">
      {isSelectable && <div className="absolute -left-2 top-1/2 transform -translate-y-1/2" data-id="element-171">
          <motion.button className={`p-2 rounded-full ${isSelected ? 'bg-purple-500 text-white' : 'bg-gray-100 text-gray-400'}`} onClick={() => onSelect?.(product.id)} whileHover={{
        scale: 1.1
      }} whileTap={{
        scale: 0.9
      }} data-id="element-172">
            <CheckIcon className="h-4 w-4" data-id="element-173" />
          </motion.button>
        </div>}
      <motion.div className="w-full md:w-24 h-24 flex-shrink-0 bg-gray-100 rounded overflow-hidden" whileHover={{
      scale: 1.05
    }} data-id="element-174">
        <img src={product.imageUrl} alt={product.title} className="w-full h-full object-cover" data-id="element-175" />
      </motion.div>
      <div className="flex-grow mt-4 md:mt-0 md:ml-4" data-id="element-176">
        <div className="flex items-start justify-between" data-id="element-177">
          <div data-id="element-178">
            <motion.h3 className="font-medium text-gray-900" whileHover={{
            color: '#2563EB'
          }} data-id="element-179">
              {product.title}
            </motion.h3>
            <p className="text-sm text-gray-500" data-id="element-180">{product.sku}</p>
          </div>
          <motion.div className="flex items-center" whileHover={{
          scale: 1.05
        }} data-id="element-181">
            {getMatchIcon(matchCategory)}
            <span className="ml-1 text-sm" data-id="element-182">{getMatchText(matchCategory)}</span>
          </motion.div>
        </div>
        <div className="mt-2" data-id="element-183">
          <p className="text-sm text-gray-600" data-id="element-184">${product.price.toFixed(2)}</p>
          <p className="text-xs text-gray-500 mt-1" data-id="element-185">
            {product.inventory} in stock • {product.category}
          </p>
        </div>
      </div>
      <div className="mt-4 md:mt-0 md:ml-4 flex flex-col items-center justify-center" data-id="element-186">
        {getActionButtons()}
      </div>
    </motion.div>;
};
export default ProductCard;